import { useEffect, useState, useCallback } from 'react'
import { message } from 'antd'
import dayjs from 'dayjs'
import {
  getLearnersReport,
  downloadLearnersReport,
} from '../../Services/Reports/LearnerReportsService'

export default function useLearnerReport() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [orderByLatest, setOrderByLatest] = useState(true)
  const [filters, setFilters] = useState({})

  const formatDate = (date) => dayjs(date).format('MM-DD-YYYY') // US format

const buildParams = useCallback(() => {
  const params = {
    PageNumber: pageNumber,
    PageSize: pageSize,
    OrderByLatest: orderByLatest,
  }

  if (filters.FromDate) params.FromDate = formatDate(filters.FromDate)
  if (filters.ToDate) params.ToDate = formatDate(filters.ToDate)

  return params
}, [pageNumber, pageSize, orderByLatest, filters])


  const fetchLearnerReport = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getLearnersReport(buildParams())
      setData(res.data?.items || [])
      setTotalCount(res.data?.totalCount || 0)
    } catch {
      message.error('Failed to load learner report')
    } finally {
      setLoading(false)
    }
  }, [buildParams])

 const downloadLearnerReport = async () => {
  const key = 'download'
  try {
    message.loading({ content: 'Generating PDF...', key })

    // Add a timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Download timed out')), 60000) // 60 seconds timeout
    );
    
    const res = await Promise.race([
      downloadLearnersReport(buildParams()),
      timeoutPromise
    ]);

    if (!res.data || res.data.size === 0) throw new Error();

    // Generate filename directly in frontend using the same pattern as backend
    const applicationName = "CoreIQPro";
    const dateStr = dayjs().format('MMM-DD-YYYY');
    const filename = `${applicationName}-Learners-Report-${dateStr}.pdf`;

    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 100);

    message.success({ content: 'PDF downloaded', key });
  } catch (error) {
    console.error("Download Error:", error);
    message.error({ content: 'Download failed', key });
  }
}

  const toggleSortOrder = () => setOrderByLatest((p) => !p)

  const resetFilters = () => {
    setFilters({})
    setOrderByLatest(true)
    setPageNumber(1)
  }

  useEffect(() => {
    fetchLearnerReport()
  }, [fetchLearnerReport])

  return {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    orderByLatest,
    setPageNumber,
    setPageSize,
    setFilters,
    toggleSortOrder,
    resetFilters,
    downloadLearnerReport,
  }
}
